/**
 * Usage Alert Components
 * 
 * Various alert components for different usage scenarios:
 * - Low usage warnings before operations
 * - Insufficient credits blocking operations
 * - Success messages after operations
 * - Pre-operation confirmation dialogs
 */

import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { 
  AlertTriangle, 
  XCircle, 
  CheckCircle, 
  Crown, 
  Gift, 
  CreditCard, 
  Clock,
  Search,
  Brain
} from 'lucide-react';
import type { UsageType } from '../../types/usage';

// ============================================================================
// Inline Alert Components
// ============================================================================

interface LowUsageWarningProps {
  usageType: UsageType;
  remainingFree: number;
  remainingCredits: number;
  onPurchaseClick: () => void;
}

export function LowUsageWarning({ 
  usageType, 
  remainingFree, 
  remainingCredits, 
  onPurchaseClick 
}: LowUsageWarningProps) {
  const getUsageIcon = () => {
    switch (usageType) {
      case 'search': return <Search className="h-4 w-4" />;
      case 'ai_analysis': return <Brain className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getUsageLabel = () => {
    switch (usageType) {
      case 'search': return 'search';
      case 'ai_analysis': return 'AI analysis';
      default: return 'operation';
    }
  };

  const getResetInfo = () => {
    switch (usageType) {
      case 'search': return 'Resets daily at midnight UTC';
      case 'ai_analysis': return 'Resets weekly on Monday';
      default: return 'Resets automatically';
    }
  };

  if (remainingFree === 0 && remainingCredits === 0) {
    return (
      <Alert className="border-red-200 bg-red-50">
        <XCircle className="h-4 w-4 text-red-500" />
        <AlertDescription className="text-red-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium mb-1">
                {getUsageLabel().charAt(0).toUpperCase() + getUsageLabel().slice(1)} unavailable
              </div>
              <div className="text-sm">
                You've reached your free {getUsageLabel()} limit and have no credits remaining.
              </div>
              <div className="text-xs mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {getResetInfo()}
              </div>
            </div>
            <Button onClick={onPurchaseClick} size="sm" className="ml-3">
              Purchase Credits
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (remainingFree === 0) {
    return (
      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <AlertDescription className="text-amber-800">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium mb-1">Free limit reached - Using credits</div>
              <div className="text-sm">
                You've used all free {getUsageLabel()}s. This operation will consume 1 credit.
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700">
                  <CreditCard className="w-3 h-3 mr-1" />
                  {remainingCredits} credits remaining
                </Badge>
              </div>
            </div>
            <Button onClick={onPurchaseClick} variant="outline" size="sm" className="ml-3">
              Buy More
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (remainingFree <= 1) {
    return (
      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <AlertDescription className="text-amber-800">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium mb-1">Low usage warning</div>
              <div className="text-sm">
                You have {remainingFree} free {getUsageLabel()}{remainingFree === 1 ? '' : 's'} remaining.
                {remainingCredits > 0 && ` After that, operations will use your ${remainingCredits} credits.`}
              </div>
              <div className="text-xs mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {getResetInfo()}
              </div>
            </div>
            {remainingCredits === 0 && (
              <Button onClick={onPurchaseClick} variant="outline" size="sm" className="ml-3">
                Buy Credits
              </Button>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}

// ============================================================================
// Success Message Components
// ============================================================================

interface UsageSuccessMessageProps {
  usageType: UsageType;
  consumedUsageType: 'free' | 'credit' | 'owner_unlimited';
  remainingFree?: number;
  remainingCredits?: number;
}

export function UsageSuccessMessage({ 
  usageType, 
  consumedUsageType, 
  remainingFree, 
  remainingCredits 
}: UsageSuccessMessageProps) {
  const getUsageLabel = () => {
    switch (usageType) {
      case 'search': return 'Search';
      case 'ai_analysis': return 'AI analysis';
      default: return 'Operation';
    }
  };

  const getSuccessIcon = () => {
    switch (consumedUsageType) {
      case 'owner_unlimited': return <Crown className="h-4 w-4 text-purple-500" />;
      case 'free': return <Gift className="h-4 w-4 text-green-500" />;
      case 'credit': return <CreditCard className="h-4 w-4 text-blue-500" />;
      default: return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
  };

  const getSuccessMessage = () => {
    switch (consumedUsageType) {
      case 'owner_unlimited':
        return `${getUsageLabel()} completed with owner unlimited access`;
      case 'free':
        return `${getUsageLabel()} completed using free usage${remainingFree !== undefined ? ` (${remainingFree} remaining)` : ''}`;
      case 'credit':
        return `${getUsageLabel()} completed using 1 credit${remainingCredits !== undefined ? ` (${remainingCredits} remaining)` : ''}`;
      default:
        return `${getUsageLabel()} completed successfully`;
    }
  };

  const getAlertStyle = () => {
    switch (consumedUsageType) {
      case 'owner_unlimited': return 'border-purple-200 bg-purple-50';
      case 'free': return 'border-green-200 bg-green-50';
      case 'credit': return 'border-blue-200 bg-blue-50';
      default: return 'border-green-200 bg-green-50';
    }
  };

  const getTextStyle = () => {
    switch (consumedUsageType) {
      case 'owner_unlimited': return 'text-purple-700';
      case 'free': return 'text-green-700';
      case 'credit': return 'text-blue-700';
      default: return 'text-green-700';
    }
  };

  return (
    <Alert className={getAlertStyle()}>
      {getSuccessIcon()}
      <AlertDescription className={getTextStyle()}>
        {getSuccessMessage()}
      </AlertDescription>
    </Alert>
  );
}

// ============================================================================
// Confirmation Dialog Components
// ============================================================================

interface UsageConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onPurchase: () => void;
  usageType: UsageType;
  willUseCredits: boolean;
  remainingCredits: number;
  remainingFree: number;
}

export function UsageConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  onPurchase,
  usageType,
  willUseCredits,
  remainingCredits,
  remainingFree
}: UsageConfirmationDialogProps) {
  const getUsageLabel = () => {
    switch (usageType) {
      case 'search': return 'search';
      case 'ai_analysis': return 'AI analysis';
      default: return 'operation';
    }
  };

  const getOperationIcon = () => {
    switch (usageType) {
      case 'search': return <Search className="w-5 h-5 text-blue-500" />;
      case 'ai_analysis': return <Brain className="w-5 h-5 text-purple-500" />;
      default: return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    }
  };

  if (remainingFree === 0 && remainingCredits === 0) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              {getUsageLabel().charAt(0).toUpperCase() + getUsageLabel().slice(1)} Unavailable
            </AlertDialogTitle>
            <AlertDialogDescription>
              You've reached your free {getUsageLabel()} limit and have no credits remaining.
              Purchase credits to continue or wait for your free usage to reset.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onPurchase}>
              Purchase Credits
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {getOperationIcon()}
            Confirm {getUsageLabel().charAt(0).toUpperCase() + getUsageLabel().slice(1)}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            {willUseCredits ? (
              <>
                <div>
                  You've used your free {getUsageLabel()} allowance. 
                  This operation will consume <strong>1 credit</strong>.
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700">
                    <CreditCard className="w-3 h-3 mr-1" />
                    {remainingCredits} credits remaining after operation
                  </Badge>
                </div>
              </>
            ) : (
              <>
                <div>
                  This operation will use <strong>1 free {getUsageLabel()}</strong>.
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 border-green-200 text-green-700">
                    <Gift className="w-3 h-3 mr-1" />
                    {remainingFree - 1} free {getUsageLabel()}s remaining after operation
                  </Badge>
                </div>
              </>
            )}
            
            {remainingFree <= 1 && remainingCredits === 0 && (
              <div className="text-amber-600 text-sm">
                ⚠️ Consider purchasing credits to avoid interruptions in the future.
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {remainingFree <= 1 && remainingCredits === 0 && (
            <Button onClick={onPurchase} variant="outline">
              Purchase Credits
            </Button>
          )}
          <AlertDialogAction onClick={onConfirm}>
            {willUseCredits ? 'Use 1 Credit' : 'Continue with Free Usage'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============================================================================
// Toast Notification Components (for use with sonner)
// ============================================================================

export const UsageToasts = {
  success: (usageType: UsageType, consumedUsageType: 'free' | 'credit' | 'owner_unlimited', remaining?: number) => {
    const operation = usageType === 'search' ? 'Search' : 'AI analysis';
    
    switch (consumedUsageType) {
      case 'owner_unlimited':
        return `👑 ${operation} completed with owner unlimited access`;
      case 'free':
        return `✅ ${operation} completed using free usage${remaining !== undefined ? ` (${remaining} remaining)` : ''}`;
      case 'credit':
        return `💳 ${operation} completed using 1 credit${remaining !== undefined ? ` (${remaining} remaining)` : ''}`;
    }
  },

  lowUsage: (usageType: UsageType, remaining: number) => {
    const operation = usageType === 'search' ? 'search' : 'AI analysis';
    return `⚠️ Only ${remaining} free ${operation}${remaining === 1 ? '' : 's'} remaining`;
  },

  noUsage: (usageType: UsageType) => {
    const operation = usageType === 'search' ? 'search' : 'AI analysis';
    return `🚫 No ${operation} usage remaining. Purchase credits to continue.`;
  },

  creditsPurchased: (amount: number, creditType: string) => {
    return `✅ ${amount} ${creditType} credits purchased successfully!`;
  }
};